import { Line, LineConfig, PlotEvent } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import {
  GET_TOTAL_IDEAS_BY_RANGE,
  TotalIdeasData,
  TotalIdeaStatsData,
} from '@frontend/data-access';
import { Card, DatePicker, Select } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { RefAttributes, useEffect, useMemo, useState } from 'react';

const { RangePicker } = DatePicker;

const filterOptions = [
  { label: 'Drafted', value: 'DRAFT' },
  { label: 'Published', value: 'PUBLISHED' },
  { label: 'Hidden', value: 'HIDDEN' },
  { label: 'Reported', value: 'REPORTED' },
];

const COLOR_PLATE_10 = ['#2FC25B', '#2888ED', '#62BAFC', '#36CBCB', '#525355'];

function TotalIdeaStats() {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const endDate = dayjs();
  const startDate = dayjs().subtract(3, 'month');
  const [data, setData] = useState<TotalIdeasData[]>([]);
  const [date, setDate] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);

  const monthNames = useMemo(
    () => [
      tcommon('Jan'),
      tcommon('Feb'),
      tcommon('Mar'),
      tcommon('Apr'),
      tcommon('May'),
      tcommon('Jun'),
      tcommon('Jul'),
      tcommon('Aug'),
      tcommon('Sep'),
      tcommon('Oct'),
      tcommon('Nov'),
      tcommon('Dec'),
    ],
    [tcommon]
  );

  const [filter, setFilter] = useState<string[]>([
    'DRAFT',
    'PUBLISHED',
    'HIDDEN',
    'REPORTED',
  ]);
  const [getData, { data: rawData, loading }] = useLazyQuery<{
    dashboardTotalIdeas: TotalIdeaStatsData;
  }>(GET_TOTAL_IDEAS_BY_RANGE, {
    fetchPolicy: 'no-cache',
    onCompleted: (rawData) => {
      const sortedData = rawData?.dashboardTotalIdeas?.list.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const result: TotalIdeasData[] = sortedData.map((state) => ({
        name: state.name,
        date: state.date,
        count: state.count,
      }));

      const aggregatedData = Object.values(
        sortedData.reduce((acc, current) => {
          if (!acc[current.date]) {
            acc[current.date] = { name: 'All', date: current.date, count: 0 };
          }
          acc[current.date].count += current.count;
          return acc;
        }, {} as { [key: string]: TotalIdeasData })
      );

      const finalResult = [...aggregatedData, ...result];

      setData(finalResult);
    },
  });

  useEffect(() => {
    if (date.length) {
      getData({
        variables: {
          startDate: date[0],
          endDate: date[1],
          statuses: filter,
        },
      });
    }
  }, [date, filter, getData]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const defaultSelectedItems = useMemo(() => {
    if (data.length > 0) {
      return data.map((item) => item.name);
    }
    return [];
  }, [data]);

  useEffect(() => {
    setSelectedItems(defaultSelectedItems);
  }, [defaultSelectedItems]);

  const config = useMemo<LineConfig & RefAttributes<unknown>>(
    () => ({
      data: data,
      loading: loading,
      xField: 'date',
      yField: 'count',
      seriesField: 'name',
      colorField: 'name',
      animate: { enter: { type: 'pathIn' } },
      style: { lineWidth: 2 },
      scale: {
        color: {
          range: COLOR_PLATE_10,
        },
      },
      axis: {
        x: {
          labelFormatter: (v) => {
            const monthIndex = parseInt(v.split('-')[1], 10) - 1;
            return monthNames[monthIndex];
          },
        },
      },
      legend: {
        color: {
          position: 'bottom',
          autoWrap: true,
          itemMarker: 'square',
          style: {
            fontSize: 14,
            fontFamily: 'Lufga',
          },
          layout: {
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
      },
      onReady: ({ chart }) => {
        chart.on('legend:filter', (e: PlotEvent) => {
          if (e?.data?.values) {
            const values = e?.data?.values as Array<string>;
            setSelectedItems([...values]);
          }
        });
      },
      interactions: [
        {
          type: 'legend-filter',
        },
      ],
      animation: {
        appear: {
          animation: 'path-in',
          duration: 5000,
        },
      },
      label: {
        text: 'count',
        position: 'top',
        textAlign: 'center',
        dy: -10,
        style: {
          fontFamily: 'Lufga',
        },
      },
    }),
    [data, loading, monthNames]
  );

  const visibleTotal = useMemo(() => {
    if (selectedItems.includes('All')) {
      return data
        .filter((item) => item.name === 'All')
        .reduce((acc, curr) => acc + curr.count, 0);
    } else {
      return data
        .filter(
          (item) => item.name !== 'All' && selectedItems.includes(item.name)
        )
        .reduce((acc, curr) => acc + curr.count, 0);
    }
  }, [data, selectedItems]);

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDate([dateStrings[0], dateStrings[1]]);
    }
  };

  const handleFilterChange = (value: string[]) => {
    setFilter(value);
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="flex items-center flex-wrap gap-1">
            <span>{t('total_during_period')}</span>
            <span className="text-gray-700 font-medium">
              / {t('total_all_time')}
            </span>
          </h2>
          <div>
            <RangePicker
              onChange={handleChange}
              defaultValue={[dayjs(date[0]), dayjs(date[1])]}
              style={{
                borderRadius: 20,
              }}
            />
          </div>
        </div>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          onChange={handleFilterChange}
          tokenSeparators={[',']}
          options={filterOptions}
          value={filter}
        />
        <div className="flex items-center flex-wrap gap-1 my-4">
          <span className="text-gray-900 text-3xl">{visibleTotal}</span>
          <span className="text-gray-700 text-2xl">
            / {rawData?.dashboardTotalIdeas?.total?.toLocaleString()}
          </span>
        </div>

        <Line {...config} />
      </div>
    </Card>
  );
}

export default TotalIdeaStats;
